// R5.2 — LAYER 6: the presentation band (spec §4.6) + the CaRMS export suppression rule (§4.10, T-CARMS-1).
//
// Bands from `PRESENTATION_BAND_COPY_IDS`, sub-elements in `SUB_ELEMENT_ORDER`, uniform typography per §1.4.
// "Not assessable" renders as an AVAILABILITY STATE, not a criticism — which is why it goes through the same
// `BandChip` as "Strong", with the same colour, weight, size and (absent) icon. Typography is the contract.
//
// §4.10 — THE CaRMS EXPORT SURFACE SUPPRESSES:
//   * the AGGREGATE presentation band, and
//   * the Navigability, Chronology, and Consistency sub-elements
// because those assess formatting the applicant does not control on that surface. Status labelling and
// Attribution clarity REMAIN. Suppression means ABSENT, never "Not assessable" (`presentationBand.ts`'s own
// note): a band we decline to compute is not a band the applicant scored badly on.
//
// The aggregate is suppressed by the ENGINE too — `presentation.overall` is null on a CaRMS surface — so this
// panel's null check and the export rule agree by construction rather than by coincidence. The suppression list
// is applied here as well, because a payload computed under `custom_cv` and later rendered on the CaRMS surface
// must still suppress; the surface is the authority for what it shows.
import type { RubricReadPublicV1 } from '../../../workers/src/lib/rubricReadPublic';
import type { InputSurface } from './copy';
import { BandChip, Copy, EvidenceReveal, LayerHeading, layerHeadingId } from './renderContent';
import { resolveEvidence, type EvidenceIndex } from './evidenceIndex';
import { BODY_STYLE, CARD_HEAD_STYLE, ENTRY_CARD_STYLE, MUTED_STYLE, SECTION_STYLE } from './styles';

type Presentation = RubricReadPublicV1['presentation'];
type SubElement = Presentation['sub_elements'][number];

/** §4.10's suppression list, by sub-element id. The two that survive are Status labelling and Attribution. */
export const CARMS_SUPPRESSED_SUB_ELEMENTS: readonly string[] = ['navigability', 'chronology', 'consistency'];

/** The one surface the suppression applies to: the non-applicant-controlled CaRMS "My Information" export. */
export function isCarmsSurface(input_surface: InputSurface): boolean {
  return input_surface === 'carms_my_information';
}

export function visibleSubElements(
  sub_elements: readonly SubElement[],
  input_surface: InputSurface,
): readonly SubElement[] {
  if (!isCarmsSurface(input_surface)) return sub_elements;
  return sub_elements.filter((s) => !CARMS_SUPPRESSED_SUB_ELEMENTS.includes(s.sub_element));
}

/** The aggregate band renders iff the engine computed one AND this is not the CaRMS surface. */
export function showsAggregateBand(presentation: Presentation, input_surface: InputSurface): boolean {
  return presentation.overall_copy_string_id !== null && !isCarmsSurface(input_surface);
}

export function PresentationPanel({
  presentation,
  input_surface,
  evidence,
}: {
  presentation: Presentation;
  input_surface: InputSurface;
  evidence: EvidenceIndex;
}) {
  const subs = visibleSubElements(presentation.sub_elements, input_surface);
  const aggregate = showsAggregateBand(presentation, input_surface) ? presentation.overall_copy_string_id : null;
  if (subs.length === 0 && aggregate === null) return null;
  return (
    <section
      aria-labelledby={layerHeadingId('CS-SECTION-PRESENTATION')}
      style={SECTION_STYLE}
      data-testid="rubric-layer-presentation"
    >
      <LayerHeading id="CS-SECTION-PRESENTATION" />
      {/* R5.7 — ONE compact card (the legacy overview's chrome): the aggregate band in the head row, the
          sub-elements as rows beneath it. Each band is still the one uniform chip (T-UNIFORM-1). */}
      <div style={ENTRY_CARD_STYLE}>
        <div style={CARD_HEAD_STYLE}>
          <p style={{ ...MUTED_STYLE, margin: 0 }}>
            <Copy id="CS-SECTION-PRESENTATION" />
          </p>
          {aggregate && (
            <p style={{ ...BODY_STYLE, margin: 0 }} data-testid="rubric-presentation-overall">
              <BandChip id={aggregate} />
            </p>
          )}
        </div>
        <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {subs.map((s) => (
            <li
              key={s.sub_element}
              style={{ margin: '0 0 var(--space-2)', paddingTop: 'var(--space-2)', borderTop: '1px solid var(--rule)' }}
              data-sub-element={s.sub_element}
            >
              <div style={CARD_HEAD_STYLE}>
                <p style={{ ...BODY_STYLE, margin: 0 }}>
                  <Copy id={s.copy_string_id} />
                </p>
                <p style={{ ...BODY_STYLE, margin: 0 }}>
                  <BandChip id={s.band_copy_string_id} />
                </p>
              </div>
              {/* §2.7 C2a — a band asserts something about the document, so it cites the layout facts behind it.
                  `applicable_items`/`satisfying_items` are NOT rendered: a proportion of the applicant's entries is
                  exactly the "count that could serve as a global proxy" §12.1 bans, and T-COUNT-1 allows one count
                  in the whole product, which is `CS-OMISSION-SCOPE`'s. */}
              <EvidenceReveal evidence={resolveEvidence(evidence, s.evidence_layout_fact_ids)} />
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
